<template>
  <div class="rg-section">
    <h4 class="rg-section__title">
      Kacheln
      <a class="fa fa-question-circle rg-tooltip-icon" v-tippy
         content="Aktionsfelder, hinter denen alle Inhalte (z.B. Gutscheine) hinterlegt werden."></a>
    </h4>

    <draggable v-model="tiles" handle=".rg-tile__header" tag="div"
               :force-fallback="true" :fallback-tolerance="5" :scroll-sensitivity="150">
      <div class="rg-tile" v-for="(tile, index) in tiles" :key="tile._key">
        <input type="hidden" :name="fieldName(index, 'id')" :value="tile.id">

        <div class="rg-tile__row">
          <div class="rg-tile__header" @click="tile.expanded = !tile.expanded">
            <i class="fa fa-arrows rg-tile__handle"></i>
            <span class="rg-tile__title">Kachel {{ index + 1 }}</span>
            <i class="fa rg-tile__chevron" :class="tile.expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </div>
          <button type="button" class="rg-tile__delete" @click="removeTile(index)">
            <i class="fa fa-trash-o"></i>
          </button>
        </div>

        <div class="rg-tile__body" v-show="tile.expanded">
          <label class="rg-label">Bezeichnung</label>
          <input type="text"
                 :name="fieldName(index, 'label')"
                 class="form-control"
                 v-model="tile.label">

          <div class="row">
            <div class="col-md-6">
              <label class="rg-label">Kann geöffnet werden von</label>
              <date-time-input v-model="tile.opens_from"/>
            </div>
            <div class="col-md-6">
              <label class="rg-label">Bis</label>
              <date-time-input v-model="tile.opens_until"/>
            </div>
          </div>

          <hr>

          <h5 class="rg-subsection__title">Darstellung</h5>
          <label class="rg-label">Anzeigeart</label>
          <segment-toggle
              v-model="tile.display_type"
              :options="[
                  {value: 'image', label: 'Bild'},
                  {value: 'color_text', label: 'Farbe und Text'},
              ]"/>

          <template v-if="tile.display_type === 'image'">
            <label class="rg-label">
              Bild hochladen (ungeöffnet)
              <a class="fa fa-question-circle rg-tooltip-icon" v-tippy content="Bild der Kachel im ungeöffneten Zustand. Quadratisch (1:1), ideal 1080 x 1080 px."></a>
            </label>
            <image-upload-with-preview
                :existing-url="tile.image_closed_url"
                v-model="tile.image_closed"
                :deleted.sync="tile.image_closed_delete"/>

            <label class="rg-label">
              Bild hochladen (geöffnet)
              <a class="fa fa-question-circle rg-tooltip-icon" v-tippy content="Bild der Kachel im geöffneten Zustand. Quadratisch (1:1), ideal 1080 x 1080 px."></a>
            </label>
            <image-upload-with-preview
                :existing-url="tile.image_open_url"
                v-model="tile.image_open"
                :deleted.sync="tile.image_open_delete"/>
          </template>

          <template v-else>
            <div class="row">
              <div class="col-md-3">
                <label class="rg-label">
                  Farbe Kachel (ungeöffnet)
                  <a class="fa fa-question-circle rg-tooltip-icon" v-tippy content="Hintergrundfarbe der Kachel im ungeöffneten Zustand."></a>
                </label>
                <input type="color"
                       :name="fieldName(index, 'color_closed')"
                       class="rg-color-input"
                       v-model="tile.color_closed">
              </div>
              <div class="col-md-9">
                <label class="rg-label">
                  Text in Kachel (ungeöffnet)
                  <a class="fa fa-question-circle rg-tooltip-icon" v-tippy content="Text auf der Kachel im ungeöffneten Zustand."></a>
                </label>
                <input type="text"
                       :name="fieldName(index, 'text_closed')"
                       class="form-control"
                       v-model="tile.text_closed">
              </div>
            </div>
            <div class="row">
              <div class="col-md-3">
                <label class="rg-label">
                  Farbe Kachel (geöffnet)
                  <a class="fa fa-question-circle rg-tooltip-icon" v-tippy content="Hintergrundfarbe der Kachel im geöffneten Zustand."></a>
                </label>
                <input type="color"
                       :name="fieldName(index, 'color_open')"
                       class="rg-color-input"
                       v-model="tile.color_open">
              </div>
              <div class="col-md-9">
                <label class="rg-label">
                  Text in Kachel (geöffnet)
                  <a class="fa fa-question-circle rg-tooltip-icon" v-tippy content="Text auf der Kachel im geöffneten Zustand."></a>
                </label>
                <input type="text"
                       :name="fieldName(index, 'text_open')"
                       class="form-control"
                       v-model="tile.text_open">
              </div>
            </div>
          </template>

          <hr>

          <h5 class="rg-subsection__title">Pop-up Inhalte</h5>
          <p class="rg-hint-text">Dieses Pop-up öffnet sich, nachdem auf die dazugehörige Kachel geklickt wurde. Dargestellt wird der Kachelinhalt in groß.</p>

          <div>
            <div class="rg-popup__box">
              <label class="rg-label">
                Bild hochladen
                <a class="fa fa-question-circle rg-tooltip-icon" v-tippy content="Bild, das im Pop-up groß dargestellt wird."></a>
              </label>
              <image-upload-with-preview
                  :existing-url="tile.popup_image_url"
                  v-model="tile.popup_image"
                  :deleted.sync="tile.popup_image_delete"/>

              <div class="rg-checkbox-row">
                <input type="checkbox"
                       :id="fieldId(index, 'popup_button_enabled')"
                       :name="fieldName(index, 'popup_button_enabled')"
                       value="1"
                       v-model="tile.popup_button_enabled">
                <label :for="fieldId(index, 'popup_button_enabled')" class="rg-checkbox-label">Button in Pop-up aktivieren</label>
              </div>

              <template v-if="tile.popup_button_enabled">
                <label class="rg-label">Button Text</label>
                <input type="text"
                       :name="fieldName(index, 'popup_button_text')"
                       class="form-control"
                       placeholder="Button Text"
                       v-model="tile.popup_button_text">

                <label class="rg-label">Button Farbe definieren</label>
                <input type="color"
                       style="max-width: 200px;"
                       :name="fieldName(index, 'popup_button_color')"
                       class="rg-color-input"
                       v-model="tile.popup_button_color">
              </template>
            </div>

            <div class="rg-checkbox-row">
              <input type="checkbox"
                     :id="fieldId(index, 'link_enabled')"
                     :name="fieldName(index, 'link_enabled')"
                     value="1"
                     v-model="tile.link_enabled">
              <label :for="fieldId(index, 'link_enabled')" class="rg-checkbox-label">Kachel verlinken</label>
            </div>

            <template v-if="tile.link_enabled">
              <label class="rg-label">Verlinkung wählen</label>
              <select :name="fieldName(index, 'link_type')" class="form-control" v-model="tile.link_type"
                      @change="tile.link_value = ''">
                <option value="">Bitte auswählen</option>
                <option v-for="option in linkTypeOptions" :key="option.id" :value="option.id">{{ option.text }}</option>
              </select>

              <template v-if="tile.link_type === 'reward_grid_part'">
                <label class="rg-label">Kampagne</label>
                <Select2 :name="fieldName(index, 'link_value')"
                         v-model="tile.link_value"
                         :options="availablePartCampaignOptions[index]"
                         placeholder="Bitte auswählen"/>
              </template>

              <template v-else-if="tile.link_type === 'magazines'">
                <label class="rg-label">Beitrag</label>
                <Select2 :name="fieldName(index, 'link_value')"
                         v-model="tile.link_value"
                         :options="contentHubOptions"
                         placeholder="Bitte auswählen"/>
              </template>

              <template v-else-if="tile.link_type === 'external'">
                <label class="rg-label">Externer Link</label>
                <input type="text"
                       :name="fieldName(index, 'link_value')"
                       class="form-control"
                       placeholder="https://..."
                       v-model="tile.link_value">
              </template>
            </template>
          </div>
        </div>
      </div>
    </draggable>

    <button type="button" class="rg-tile-add" @click="addTile">
      <i class="fa fa-plus"></i> Neue Kachel hinzufügen
    </button>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import SegmentToggle from '../../common/segment-toggle/SegmentToggle.vue';
import ImageUploadWithPreview from '../../common/image-upload-with-preview/ImageUploadWithPreview.vue';
import DateTimeInput from '../../common/date-time-input/DateTimeInput.vue';

export default {
  name: 'RewardGridTiles',
  components: {draggable, SegmentToggle, ImageUploadWithPreview, DateTimeInput},
  props: {
    dbRecord: {type: Array, default: () => []},
    partOfRewardGridCampaigns: {type: Array, default: () => []},
    contentHubItems: {type: Array, default: () => []},
  },
  data() {
    return {
      tiles: this.dbRecord.length > 0 ? this.dbRecord.map(this.hydrateTile) : [this.newTile()],
      linkTypeOptions: [
        {id: 'reward_grid_part', text: 'Vorteilskalender Teilkampagne'},
        {id: 'magazines', text: 'WKZ-Trendblog'},
        {id: 'external', text: 'Extern'},
      ],
    }
  },
  computed: {
    partOfRewardGridCampaignOptions() {
      return this.partOfRewardGridCampaigns.map(campaign => ({id: campaign.id, text: campaign.name_internal}));
    },
    // A part campaign belongs to exactly one tile: its opened state is stored per campaign,
    // so a second tile on the same campaign would show up as already opened.
    availablePartCampaignOptions() {
      const takenPerTile = this.tiles.map(tile =>
        tile.link_enabled && tile.link_type === 'reward_grid_part' && tile.link_value
          ? String(tile.link_value)
          : null
      );

      return this.tiles.map((tile, index) => {
        const taken = new Set(takenPerTile.filter((value, i) => value !== null && i !== index));

        return this.partOfRewardGridCampaignOptions.filter(option => !taken.has(String(option.id)));
      });
    },
    contentHubOptions() {
      return this.contentHubItems.map(item => ({id: item.id, text: item.news_title}));
    },
  },
  methods: {
    newTile() {
      return {
        id: null,
        _key: window.uuid.v4(),
        expanded: true,
        label: '',
        opens_from: '',
        opens_until: '',
        display_type: 'image',
        image_closed: null,
        image_closed_url: null,
        image_closed_delete: false,
        image_open: null,
        image_open_url: null,
        image_open_delete: false,
        color_closed: '#02b8a2',
        color_open: '#02b8a2',
        text_closed: '',
        text_open: '',
        popup_image: null,
        popup_image_url: null,
        popup_image_delete: false,
        popup_button_enabled: false,
        popup_button_text: '',
        popup_button_color: '#000000',
        link_enabled: false,
        link_type: '',
        link_value: '',
      }
    },
    hydrateTile(record) {
      // link_type comes back as null for never-linked tiles, which matches no option.
      return {...this.newTile(), ...record, link_type: record.link_type ?? '', expanded: false};
    },
    addTile() {
      this.tiles.forEach(tile => tile.expanded = false);
      this.tiles.push(this.newTile());
    },
    removeTile(index) {
      if (confirm(`Kachel ${index + 1} wirklich löschen?`)) {
        this.tiles.splice(index, 1);
      }
    },
    fieldName(index, field) {
      return `reward_grid_tiles[${index}][${field}]`;
    },
    fieldId(index, field) {
      return `reward_grid_tiles_${index}_${field}`;
    },
    // Ids come back in tile order, so a retry after a failed upload updates instead of duplicating.
    assignIds(ids) {
      this.tiles.forEach((tile, index) => tile.id = ids[index]);
    },
    pendingMedia() {
      return this.tiles.flatMap(tile =>
        ['image_closed', 'image_open', 'popup_image']
          .filter(collection => tile[collection])
          .map(collection => ({tileId: tile.id, collection, file: tile[collection]}))
      );
    },
    appendToFormData(formData) {
      this.tiles.forEach((tile, index) => {
        const prefix = `reward_grid_tiles[${index}]`;

        if (tile.id) {
          formData.append(`${prefix}[id]`, tile.id);
        }

        formData.append(`${prefix}[label]`, tile.label || '');
        formData.append(`${prefix}[opens_from]`, tile.opens_from || '');
        formData.append(`${prefix}[opens_until]`, tile.opens_until || '');
        formData.append(`${prefix}[display_type]`, tile.display_type || '');
        formData.append(`${prefix}[color_closed]`, tile.color_closed || '');
        formData.append(`${prefix}[color_open]`, tile.color_open || '');
        formData.append(`${prefix}[text_closed]`, tile.text_closed || '');
        formData.append(`${prefix}[text_open]`, tile.text_open || '');

        formData.append(`${prefix}[image_closed_delete]`, tile.image_closed_delete ? '1' : '0');
        formData.append(`${prefix}[image_open_delete]`, tile.image_open_delete ? '1' : '0');
        formData.append(`${prefix}[popup_image_delete]`, tile.popup_image_delete ? '1' : '0');

        formData.append(`${prefix}[popup_button_enabled]`, tile.popup_button_enabled ? '1' : '0');
        formData.append(`${prefix}[popup_button_text]`, tile.popup_button_text || '');
        formData.append(`${prefix}[popup_button_color]`, tile.popup_button_color || '');

        formData.append(`${prefix}[link_enabled]`, tile.link_enabled ? '1' : '0');
        formData.append(`${prefix}[link_type]`, tile.link_type || '');
        formData.append(`${prefix}[link_value]`, tile.link_value || '');
      });
    },
  },
}
</script>

<style scoped>
.rg-section {
  padding: 16px 0;
}

.rg-section__title {
  font-weight: 700;
  margin: 0 0 16px;
}

.rg-subsection__title {
  font-weight: 700;
  margin: 16px 0;
}

.rg-tooltip-icon {
  cursor: pointer;
  color: #007bff;
}

.rg-label {
  display: block;
  font-weight: 700;
  margin: 16px 0 8px;
}

.rg-color-input {
  display: block;
  width: 100%;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 4px;
}

.rg-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}

.rg-checkbox-label {
  font-weight: 700;
  margin: 0;
  cursor: pointer;
}

.rg-hint-text {
  color: #888;
  margin: 0 0 16px;
}

.rg-tile {
  margin-bottom: 12px;
}

.rg-tile__row {
  display: flex;
  gap: 8px;
}

.rg-tile__header {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #e3e3e3;
  border-radius: 4px;
  padding: 14px 16px;
  cursor: grab;
  user-select: none;
}

.rg-tile__handle {
  color: #666;
}

.rg-tile__title {
  flex: 1 1 0;
  font-weight: 700;
}

.rg-tile__chevron {
  color: #666;
}

.rg-tile__delete {
  flex: 0 0 auto;
  border: none;
  border-radius: 4px;
  background-color: #e2536b;
  color: #fff;
  width: 48px;
  cursor: pointer;
}

.rg-tile__body {
  padding: 12px;
  border: 1px solid #ccc;
  border-top: none;
}

.rg-popup__box {
  background-color: #f2f2f2;
  border-radius: 4px;
  padding: 16px;
}

.rg-tile-add {
  display: block;
  width: 100%;
  border: none;
  border-radius: 4px;
  background-color: #2f80c4;
  color: #fff;
  padding: 8px;
  margin-top: 16px;
  cursor: pointer;
}
</style>
